/**
 * Sportsbook Account Service
 * Manages linking and tracking of user sportsbook accounts in Firestore
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export const SportsbookAccountService = {
  /**
   * Link a sportsbook account for a user
   * @param {string} userId - Firebase user ID
   * @param {string} sportsbookId - Sportsbook identifier (e.g., 'draftkings', 'fanduel')
   * @param {string} sportsbookName - Display name of sportsbook
   * @param {string} accountEmail - User's account email/username for the sportsbook
   * @returns {Promise<boolean>} Success status
   */
  async linkAccount(userId, sportsbookId, sportsbookName, accountEmail) {
    try {
      // Create a unique document ID combining userId and sportsbookId
      const docId = `${userId}_${sportsbookId}`;
      const accountRef = doc(db, 'linkedSportsbooks', docId);
      
      const accountData = {
        userId,
        sportsbookId,
        sportsbookName,
        accountEmail: accountEmail || '',
        status: 'connected',
        linkedAt: Timestamp.now(),
        lastUpdated: Timestamp.now()
      };
      
      await setDoc(accountRef, accountData);
      console.log(`✅ Successfully linked ${sportsbookName} account for user ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error linking sportsbook account:', error);
      throw error;
    }
  },

  /**
   * Unlink a sportsbook account
   * @param {string} userId - Firebase user ID
   * @param {string} sportsbookId - Sportsbook identifier
   * @returns {Promise<boolean>} Success status
   */
  async unlinkAccount(userId, sportsbookId) {
    try {
      const docId = `${userId}_${sportsbookId}`;
      const accountRef = doc(db, 'linkedSportsbooks', docId);
      
      await deleteDoc(accountRef);
      console.log(`✅ Successfully unlinked ${sportsbookId} account for user ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error unlinking sportsbook account:', error);
      throw error;
    }
  },

  /**
   * Get all linked accounts for a user
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Array>} Array of linked account objects
   */
  async getLinkedAccounts(userId) {
    try {
      const linkedAccountsRef = collection(db, 'linkedSportsbooks');
      const q = query(linkedAccountsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      const accounts = [];
      querySnapshot.forEach((doc) => {
        accounts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`✅ Retrieved ${accounts.length} linked accounts for user ${userId}`);
      return accounts;
    } catch (error) {
      console.error('❌ Error getting linked accounts:', error);
      throw error;
    }
  },

  /**
   * Get a specific linked account
   * @param {string} userId - Firebase user ID
   * @param {string} sportsbookId - Sportsbook identifier
   * @returns {Promise<Object|null>} Linked account object or null if not found
   */
  async getLinkedAccount(userId, sportsbookId) {
    try {
      const docId = `${userId}_${sportsbookId}`;
      const accountRef = doc(db, 'linkedSportsbooks', docId);
      const accountDoc = await getDoc(accountRef);
      
      if (accountDoc.exists()) {
        return {
          id: accountDoc.id,
          ...accountDoc.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error getting linked account:', error);
      throw error;
    }
  },

  /**
   * Update account status
   * @param {string} userId - Firebase user ID
   * @param {string} sportsbookId - Sportsbook identifier
   * @param {string} status - New status ('connected' or 'disconnected')
   * @returns {Promise<boolean>} Success status
   */
  async updateAccountStatus(userId, sportsbookId, status) {
    try {
      const docId = `${userId}_${sportsbookId}`;
      const accountRef = doc(db, 'linkedSportsbooks', docId);
      
      await updateDoc(accountRef, {
        status,
        lastUpdated: Timestamp.now()
      });
      
      console.log(`✅ Updated ${sportsbookId} account status to ${status} for user ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error updating account status:', error);
      throw error;
    }
  },

  /**
   * Check if a sportsbook is linked for a user
   * @param {string} userId - Firebase user ID
   * @param {string} sportsbookId - Sportsbook identifier
   * @returns {Promise<boolean>} True if linked, false otherwise
   */
  async isLinked(userId, sportsbookId) {
    try {
      const account = await this.getLinkedAccount(userId, sportsbookId);
      return account !== null && account.status === 'connected';
    } catch (error) {
      console.error('❌ Error checking if account is linked:', error);
      return false;
    }
  }
};

export default SportsbookAccountService;
